import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  useDispatch,
  useSelector,
} from "react-redux";
import {
  useNavigate,
  useParams,
} from "react-router-dom";
import { createOrder } from "../../actions/orderActions";
import { fetchPriceSummary } from "../../actions/priceActions";
import { useCart } from "../../components/CartContext";
import {
  useRestaurantSession,
} from "../../components/RestaurantSessionProvider";
import {
  isSessionExpiredError,
  getSessionExpiredMessage,
} from "../../utils/sessionErrorHandler";
import "./css/OrderScreen.css";
function OrderScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    restaurantId,
    tableId,
  } = useParams();
  // =====================================================
  // DINING SESSION
  // =====================================================
  const {
    sessionToken,
  } = useRestaurantSession();
  // =====================================================
  // CART
  // =====================================================
  const {
    cart,
    cartCount,
      clearCart,
  } = useCart();
  // =====================================================
  // REDUX
  // =====================================================
  const {
    loading,
    error,
    errorCode,
  } = useSelector(
    (state) => state.order
  );
  // =====================================================
  // SERVER PRICE SUMMARY
  // =====================================================
  const {
    loading: priceLoading,
    summary: priceSummary,
    error: priceError,
  } = useSelector((state) => state.price);

  useEffect(() => {
    if (cart.length === 0) return;
    const items = cart.map((item) => ({
      menu_item_id: item.id,
      quantity: item.quantity,
    }));
    dispatch(fetchPriceSummary(restaurantId, items));
  }, [dispatch, restaurantId, cart]);

  // =====================================================
  // FORM
  // =====================================================
  const [
    paymentMethod,
    setPaymentMethod
  ] = useState("CASH");
  const [
    notes,
    setNotes
  ] = useState("");
  const [paymentOptions, setPaymentOptions] = useState(null);
  useEffect(() => {
    let live = true;
    setPaymentOptions(null);
    axios.get(`/api/restaurants/${encodeURIComponent(restaurantId)}/payment-options/`)
      .then(({data}) => {
        if (!live) return;
        setPaymentOptions(data);
        setPaymentMethod((current) => {
          if (current === "ONLINE" && !data.allow_online_payment) return data.allow_cash_payment ? "CASH" : "";
          if (current === "CASH" && !data.allow_cash_payment) return data.allow_online_payment ? "ONLINE" : "";
          return current;
        });
      })
      .catch(() => { if (live) setPaymentOptions({allow_cash_payment:false, allow_online_payment:false}); });
    return () => { live = false; };
  }, [restaurantId]);
  // =====================================================
  // CREATE ORDER
  // =====================================================
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!paymentOptions || !paymentOptions[`allow_${paymentMethod.toLowerCase()}_payment`]) {
      alert("Selected payment method is unavailable. Please choose an enabled method.");
      return;
    }
    // ---------------------------------------------------
    // CHECK CART
    // ---------------------------------------------------
    if (cart.length === 0) {
      alert(
        "Your cart is empty."
      );
      navigate(
        `/${restaurantId}/table/${tableId}`
      );
      return;
    }
    // ---------------------------------------------------
    // CHECK DINING SESSION
    // ---------------------------------------------------
    if (!sessionToken) {
      alert(
        "Dining session is not ready. Please try again."
      );
      return;
    }
    // ---------------------------------------------------
    // ---------------------------------------------------
  // REQUIRE SERVER PRICE BEFORE SUBMITTING
  // ---------------------------------------------------
  if (priceLoading || priceError || !priceSummary) {
    alert("Final price is not ready. Please wait or try again.");
    return;
  }

  // ORDER DATA
    // ---------------------------------------------------
    const orderData = {
      items: cart.map((item) => ({
        menu_item_id:
          item.id,
        quantity:
          item.quantity,
      })),
      // -------------------------------------------------
      // DINING SESSION
      // -------------------------------------------------
      session_token:
        sessionToken,
      payment_method:
        paymentMethod,
      table_id:
        Number(tableId),
      notes:
        notes,
    };
    console.log(
      "Order request:",
      orderData
    );
    // ---------------------------------------------------
    // CREATE ORDER
    // ---------------------------------------------------
    try {
      const createdOrder =
        await dispatch(
          createOrder(
            restaurantId,
            orderData
          )
        );
      console.log(
        "Created order:",
        createdOrder
      );
      // =================================================
      // CASH PAYMENT
      // =================================================
      if (
        paymentMethod === "CASH"
      ) {
        /*
         * Order was successfully created.
         * Clear the cart now.
         */
        clearCart();
        navigate(
          `/${restaurantId}/table/${tableId}/success`
        );
        return;
      }
      // =================================================
      // ONLINE PAYMENT
      // =================================================
      /*
       * Do NOT clear the cart yet.
       *
       * The order is created, but online payment
       * has not been verified yet.
       *
       * PaymentScreen will clear the cart after
       * successful Razorpay verification.
       */
      navigate(
        `/${restaurantId}/table/${tableId}/payment`
      );
    } catch (error) {
      console.error(
        "Order creation error:",
        error
      );
      // -------------------------------------------------
      // SESSION EXPIRED
      // -------------------------------------------------
      if (
        isSessionExpiredError(error)
      ) {
        console.log(
          "Dining session expired."
        );
        return;
      }
      // -------------------------------------------------
      // OTHER ERRORS
      // -------------------------------------------------
      console.error(
        getSessionExpiredMessage(error)
      );
    }
  };
  // =====================================================
  // PAGE
  // =====================================================
  return (
    <div className="order-page">
      <div className="order-container">
        {/* HEADER */}
        <div className="order-header">
          <button
            type="button"
            onClick={() =>
              navigate(
                `/${restaurantId}/table/${tableId}/checkout`
              )
            }
          >
            ← Back
          </button>
          <h1>
            Create Order
          </h1>
          <p>
            Review your order and continue
          </p>
        </div>
        {/* ERROR */}
        {error && (
          <div className="order-error">
            {error}
          </div>
        )}
        {/* SESSION EXPIRED MESSAGE */}
        {(
          errorCode ===
          "SESSION_EXPIRED"
        ) && (
          <div className="order-error">
            Your table session has ended.
            Please scan the table QR code
            again to start a new session.
          </div>
        )}
        {/* FORM */}
        <form
          className="order-form"
          onSubmit={handleSubmit}
        >
          {/* PAYMENT METHOD */}
          <div className="order-section">
            <h2>
              Payment Method
            </h2>
            <div className="order-payment-options">
              {/* CASH */}
              <label>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="CASH"
                  disabled={!paymentOptions?.allow_cash_payment}
                  checked={
                    paymentMethod === "CASH"
                  }
                  onChange={(event) =>
                    setPaymentMethod(
                      event.target.value
                    )
                  }
                />
                <span>
                  Cash
                </span>
              </label>
              {/* ONLINE */}
              <label style={!paymentOptions?.allow_online_payment ? {opacity:0.45, cursor:"not-allowed"} : undefined}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="ONLINE"
                  disabled={!paymentOptions?.allow_online_payment}
                  checked={
                    paymentMethod === "ONLINE"
                  }
                  onChange={(event) =>
                    setPaymentMethod(
                      event.target.value
                    )
                  }
                />
                <span>
                  Online Payment
                </span>
              </label>
            </div>
          </div>
          
                  {/* SUMMARY */}
        <div className="order-summary">
          <div>
            Items
            <strong>{cartCount}</strong>
          </div>
          <div>
            Subtotal
            <strong>{priceLoading ? "Calculating..." : priceSummary ? `₹${Number(priceSummary.subtotal).toFixed(2)}` : "—"}</strong>
          </div>
          {priceSummary && (
            <>
              <div>
                Tax
                <strong>₹{Number(priceSummary.tax || 0).toFixed(2)}</strong>
              </div>
              <div>
                Service Charge
                <strong>₹{Number(priceSummary.service_charge || 0).toFixed(2)}</strong>
              </div>
              {Number(priceSummary.discount || 0) > 0 && (
                <div>
                  Discount
                  <strong>-₹{Number(priceSummary.discount).toFixed(2)}</strong>
                </div>
              )}
            </>
          )}
          <div className="order-total">
            <span>Total</span>
            <strong>{priceLoading ? "Calculating..." : priceSummary ? `₹${Number(priceSummary.total).toFixed(2)}` : "—"}</strong>
          </div>
          {priceError && <div className="order-error">{priceError}</div>}
        </div>
{/* CREATE ORDER */}
          <button
            type="submit"
            className="create-order-button"
            disabled={loading || priceLoading || !!priceError || !priceSummary || !paymentOptions || !paymentOptions[`allow_${paymentMethod.toLowerCase()}_payment`]}
          >
            {loading
              ? "Creating Order..."
              : "Create Order →"}
          </button>
        </form>
      </div>
    </div>
  );
}
export default OrderScreen;